import { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { getUserStorageKey } from '@/lib/storage/localStorage';
import { selectUserId } from '@/redux/user/selectors';

const ALERT_PREFIX = 'alertDismissed:';

export const DISMISSABLE_ALERT_IDS = { COLLECTIONS_DETAILS: 'collections-details' } as const;

export type DismissableAlertId = (typeof DISMISSABLE_ALERT_IDS)[keyof typeof DISMISSABLE_ALERT_IDS];

/**
 * Custom hook for managing dismissable alert state with localStorage persistence
 * @param id - The unique alert identifier from ALERT_IDS
 * @param defaultVisible - Whether the alert should be visible by default (defaults to true)
 * @returns Object containing the alert's visibility state and dismiss function
 * @example
 * ```ts
 * const { visible, dismiss } = useDismissableAlert(ALERT_IDS.COLLECTIONS_DETAILS);
 *
 * return (
 *   <>
 *     {visible && (
 *       <Alert onDismiss={dismiss}>
 *         Your alert content here
 *       </Alert>
 *     )}
 *   </>
 * );
 * ```
 */
export function useDismissableAlert(
  id: DismissableAlertId,
  defaultVisible = true
): { visible: boolean; dismiss: () => void } {
  const userId = useSelector(selectUserId);
  const key = getUserStorageKey(userId);
  const [visible, setVisible] = useState<boolean>(defaultVisible);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed[`${ALERT_PREFIX}${id}`]) {
          setVisible(false);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [key, id, defaultVisible]);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      const stored = window.localStorage.getItem(key);
      const parsed = stored ? JSON.parse(stored) : {};
      window.localStorage.setItem(
        key,
        JSON.stringify({ ...parsed, [`${ALERT_PREFIX}${id}`]: true })
      );
    } catch (error) {
      console.error(error);
    }
  }, [id, key]);

  return { visible, dismiss };
}
